import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import boTheme from '@ecp/common/src/style/theme/bo';
import { FlexBox, FlexBoxCenter } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import Tooltip from '@ecp/common/src/components/tooltip/bo/Tooltip';
import { ReactComponent as QuestionIcon } from '@ecp/common/src/assets/icon/icon__tooltip-question.svg';

import { withMenuUrl } from '@bo-recoil/common/selector';
import { pageBackPathState, pageTitleState, pageTooltipState } from '@bo-recoil/common/atom';
import useSearchOption from '@bo-hooks/common/useSearchOption';
import { useBookmarkMenu } from '@bo-hooks/common/useBookmarkMenu';
import { ContentTitle } from '@bo-components/common/styled';
import { ReactComponent as BookmarkSelectedIcon } from '@bo-assets/icon/common/icon__bookmark--selected.svg';
import { ReactComponent as BookmarkIcon } from '@bo-assets/icon/common/icon__bookmark.svg';
import { ReactComponent as BackIcon } from '@bo-assets/icon/common/icon__back.svg';

const ContentsPageTitle = (props) => {
  const { menuUrl } = props;
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const customTitle = useRecoilValue(pageTitleState);
  const backPath = useRecoilValue(pageBackPathState);

  const exactMenu = useRecoilValue(withMenuUrl(menuUrl));
  const abstractMenu = useRecoilValue(withMenuUrl(pathname));
  const currentMenu = useMemo(() => exactMenu ?? abstractMenu, [abstractMenu, exactMenu]);

  const tooltip = useRecoilValue(pageTooltipState);
  const { updateBookmarkMenu } = useBookmarkMenu(currentMenu?.menuNumber);
  const { searchOptionState } = useSearchOption();

  const handleBookmarkClick = useCallback(async () => {
    await updateBookmarkMenu(!currentMenu?.isBookmark);
  }, [currentMenu?.isBookmark, updateBookmarkMenu]);

  const handleClickGoBack = useCallback(() => {
    navigate(backPath, searchOptionState);
  }, [backPath, navigate, searchOptionState]);

  const pageTitle = useMemo(() => {
    return customTitle?.length > 0 ? customTitle : currentMenu?.menuName;
  }, [currentMenu, customTitle]);

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <Container>
      {currentMenu ? (
        <BookmarkIconWrapper onClick={handleBookmarkClick}>
          {currentMenu.isBookmark ? <BookmarkSelectedIcon /> : <BookmarkIcon />}
        </BookmarkIconWrapper>
      ) : (
        <HistoryBackButton onClick={handleClickGoBack}>
          <BackIcon />
        </HistoryBackButton>
      )}
      <Spacing right={4} />
      <ContentTitle>{pageTitle}</ContentTitle>
      {tooltip && (
        <Tooltip arrow placement={'bottom-start'} value={tooltip}>
          <HelpIcon />
        </Tooltip>
      )}
    </Container>
  );
};

ContentsPageTitle.propTypes = {
  menuUrl: PropTypes.string,
};

export default ContentsPageTitle;

const Container = styled(FlexBox)`
  height: 52px;
  flex-grow: 0;
  flex-basis: fit-content;
  flex-shrink: 1;
  padding: 17px 0 13px;
  position: sticky;
  top: 0;
  z-index: 6;

  background-color: ${boTheme.color.sub10};
`;

const BookmarkIconWrapper = styled(FlexBox)`
  width: 24px;
  height: 22px;
  cursor: pointer;
`;

const HistoryBackButton = styled(FlexBoxCenter)`
  width: 24px;
  height: 22px;
  align-items: center;
  cursor: pointer;
`;

const HelpIcon = styled(QuestionIcon)`
  width: 18px;
  height: 18px;
  margin-left: 6px;
  flex-shrink: 0;
  cursor: pointer;
`;
